import { z } from 'zod';

import { metadata } from '@nesso/common-contracts/schema-metadata';
import { US_STATE_NAMES } from '@nesso/common-contracts/us-states';

const MAX_FIELD_LENGTH = 256;

const getErrorMessage = (fieldName: string) =>
    `${fieldName} must be at most ${MAX_FIELD_LENGTH} characters`;

export const practiceNameSchema = z
    .string()
    .trim()
    .min(1, 'Enter practice name')
    .max(MAX_FIELD_LENGTH, getErrorMessage('Practice name'))
    .meta(metadata.nonSensitive);

const EMAIL_MESSAGE = 'Enter a valid email address (e.g., name@example.com)';

export const businessEmailSchema = z
    .string()
    .transform((value) => value.trim())
    .pipe(z.email(EMAIL_MESSAGE).max(MAX_FIELD_LENGTH, EMAIL_MESSAGE))
    .meta(metadata.nonSensitive);

// Any 10-digit US number is accepted, including Google Voice numbers — no area-code/NANP checks.
export const businessPhoneSchema = z
    .string()
    .transform((value) => value.replace(/[\s().+-]/g, ''))
    .transform((digits) =>
        digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits,
    )
    .pipe(z.string().regex(/^\d{10}$/, 'Enter valid 10-digit phone number'))
    .meta(metadata.nonSensitive);

const TAX_ID_MESSAGE =
    'Enter valid 9-digit tax ID. To protect your identity, we recommend using your EIN, not your SSN.';

// No `nonSensitive` — this is the one field the sanitizer must strip from every Access entry.
export const taxIdSchema = z
    .string()
    .regex(/^(\d{2}-\d{7}|\d{3}-\d{2}-\d{4}|\d{9})$/, TAX_ID_MESSAGE)
    .transform((value) => value.replace(/-/g, ''))
    .pipe(z.string().regex(/^\d{9}$/, TAX_ID_MESSAGE));

const requiredLocationField = (fieldName: string, emptyMessage: string) =>
    z
        .string()
        .trim()
        .min(1, emptyMessage)
        .max(MAX_FIELD_LENGTH, getErrorMessage(fieldName))
        .meta(metadata.nonSensitive);

// Stored value: a location's state is the full name, not the code — the two-letter abbreviation
// is display-only, derived client-side from this same table.
export { US_STATE_NAMES, US_STATES, type UsStateName } from '@nesso/common-contracts/us-states';

export const APPOINTMENT_MODALITIES = ['IN_PERSON', 'TELEHEALTH'] as const;

export const appointmentModalitySchema = z.enum(APPOINTMENT_MODALITIES);

export type AppointmentModality = z.infer<typeof appointmentModalitySchema>;

// CMS place-of-service codes offered for a practice location, in display order.
// The stored value is the two-character code, leading zero included.
export const PLACE_OF_SERVICE_CODES = [
    '02',
    '03',
    '04',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '21',
    '22',
    '23',
    '25',
    '26',
    '27',
    '31',
    '32',
    '33',
    '34',
    '51',
    '52',
    '53',
    '54',
    '55',
    '56',
    '57',
] as const;

export const placeOfServiceCodeSchema = z.enum(PLACE_OF_SERVICE_CODES, {
    error: 'Select place of service',
});

export type PlaceOfServiceCode = z.infer<typeof placeOfServiceCodeSchema>;

export const PLACE_OF_SERVICE_LABELS: Record<PlaceOfServiceCode, string> = {
    '02': 'Telehealth Provided Other than in Patient’s Home',
    '03': 'School',
    '04': 'Homeless Shelter',
    '10': 'Telehealth Provided in Patient’s Home',
    '11': 'Office',
    '12': 'Home',
    '13': 'Assisted Living Facility',
    '14': 'Group Home',
    '15': 'Mobile Unit',
    '21': 'Inpatient Hospital',
    '22': 'On Campus – Outpatient Hospital',
    '23': 'Emergency Room – Hospital',
    '25': 'Birthing Center',
    '26': 'Military Treatment Facility',
    '27': 'Outreach Site/Street',
    '31': 'Skilled Nursing Facility',
    '32': 'Nursing Facility',
    '33': 'Custodial Care Facility',
    '34': 'Hospice',
    '51': 'Inpatient Psychiatric Facility',
    '52': 'Psychiatric Facility – Partial Hospitalization',
    '53': 'Community Mental Health Center',
    '54': 'Intermediate Care Facility/Individuals with Intellectual Disabilities',
    '55': 'Residential Substance Abuse Treatment Facility',
    '56': 'Psychiatric Residential Treatment Center',
    '57': 'Non-residential Substance Abuse Treatment Facility',
};

// A ZodObject's `.superRefine()` still returns a ZodObject in Zod v4, so `.extend()` remains
// available afterwards and the cross-field check below still runs on the extended schema.
export const practiceLocationDetailsSchema = z
    .object({
        name: requiredLocationField(
            'Name',
            'Enter location name. Use the neighborhood, city, or street to make this location easy to distinguish from others',
        ),
        street1: requiredLocationField('Street address', 'Enter street address'),
        street2: z
            .string()
            .trim()
            .max(MAX_FIELD_LENGTH, getErrorMessage('Suite / floor / office'))
            .nullish()
            .transform((value) => (value ? value : null))
            .meta(metadata.nonSensitive),
        city: requiredLocationField('City', 'Enter city'),
        state: z.enum(US_STATE_NAMES, { error: 'Select state' }).meta(metadata.nonSensitive),
        zip: z
            .string()
            .regex(/^\d{5}$/, 'Enter 5-digit zip')
            .meta(metadata.nonSensitive),
        placeOfServiceCode: placeOfServiceCodeSchema.meta(metadata.nonSensitive),
        // Required — a missing answer is reported the same as an explicit refusal.
        offersTelehealth: z.boolean({ error: 'Select yes or no' }).meta(metadata.nonSensitive),
        // Non-null iff offersTelehealth; the pairing is asserted below.
        defaultAppointmentModality: appointmentModalitySchema
            .nullable()
            .meta(metadata.nonSensitive),
    })
    .superRefine(
        (value, ctx) => {
            if (value.offersTelehealth && value.defaultAppointmentModality === null) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Select default',
                    path: ['defaultAppointmentModality'],
                });
            }
            if (!value.offersTelehealth && value.defaultAppointmentModality !== null) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Default modality must be empty when telehealth is not offered',
                    path: ['defaultAppointmentModality'],
                });
            }
        },
        {
            // Zod v4 still runs a superRefine after a sibling field fails, unless that check's own
            // input aborted; `when` covers the offersTelehealth-failed case so "Select default" isn't deferred to a second submit.
            when: (payload) =>
                typeof (payload.value as { offersTelehealth?: unknown } | undefined)
                    ?.offersTelehealth === 'boolean',
        },
    );
